import Source from "../sources/Source";
import SourceTextCache from "../sources/SourceTextCache";
import VersionRenderer from "./VersionRenderer";
import VersionRenderStyle from "./VersionRenderStyle";
import VersionStyle, { InterlinearNumberStyle } from "./VersionStyle";
import VersionStyles from "./VersionStyles";
import WordHighlighter from "./WordHighlighter";
import WordRenderer from "./WordRenderer";
import WordStyles from "./WordStyles";

export default class SourceRenderer implements VersionRenderer {

    constructor(private readonly source: Source, private readonly style: VersionStyle) {
    }

    public render(verseIndex: number, sourceTextCache: SourceTextCache, style?: VersionRenderStyle): string {
        const versionStyle = VersionStyles.mergeStyles(this.style, style || {});

        const wordRenderer = new WordRenderer({
            interlinearNumberStyle: WordStyles.copyVersionInterlinearNumberStyle(versionStyle),
            lexiconLookup: versionStyle.allowLexiconLookup ? {
                sourceIndex: this.source.getIndex(),
                verseIndex
            } : undefined,
            partSeparator: versionStyle.wordPartSeparator,
            tooltipGenerator: versionStyle.wordTooltipGenerator
        });

        const verseText = sourceTextCache.getText(verseIndex, this.source.getIndex());
        if (verseText === undefined) {
            return '';
        }

        let text;
        const classes = [];
        if (verseText !== '') {
            const words = verseText.split(' ');
            const highlightWordIndexes = style && style.highlightQuery ?
                WordHighlighter.getHighlightIndexes(words, style.highlightQuery) : [];
            const renderedWords = this.renderWords(words, wordRenderer, versionStyle);
            const highlightedWords = WordHighlighter.highlightWords(renderedWords, highlightWordIndexes);
            text = highlightedWords.join(' ');
            if (versionStyle.classes) {
                classes.push(...versionStyle.classes);
            }
        } else {
            text = 'Omitted';
            classes.push('_Omitted');
        }

        const classAttr = classes.length > 0 ? ` class="${classes.join(' ')}"` : '';
        return `<div${classAttr}>${text}</div>`;
    }

    private renderWords(words: string[], wordRenderer: WordRenderer, style: VersionStyle): string[] {
        let nextInterlinearNumber = 1;

        return words.map((word, index) => {
            let foundInterlinear = false;

            word = word.replace(/\B%([0-9]+)(?:|:(.*?))%\B/, (match, group1, group2) => {
                foundInterlinear = true;
                if (style.interlinearNumberStyle === InterlinearNumberStyle.ReplaceNonSequential) {
                    const classes = ['_InterlinearNumber'];
                    if (group2) {
                        classes.push('_AlternateInterlinearNumber');
                    }
                    return `<span class="${classes.join(' ')}">${group1}</span>`;
                } else {
                    return '';
                }
            });

            if (!foundInterlinear) {
                word = word.replace(/\B%\B/, () => {
                    foundInterlinear = true;
                    if (style.interlinearNumberStyle === InterlinearNumberStyle.ReplaceSequential) {
                        return `<span class="_InterlinearNumber">${nextInterlinearNumber++}</span>`;
                    } else {
                        return '';
                    }
                });
            }

            return !foundInterlinear ? wordRenderer.renderWord(word, index) : word;
        });
    }
}
